import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import type {FormulaMeasurement, FormulaShowClause, ShowClauseFormula} from 'common/report/insights/util/types';
import type {FormulaShowClause as FormulaShowClauseClass} from 'common/report/insights/models/show-clause';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import type {QueryFormulaEntry} from 'common/query-builder/query-entry-section/types';
import {MultiAttribution} from 'common/widgets/multi-attribution-menu/types';
import {BehaviorShowClause} from 'common/report/insights/util/types';

import {ModelStatus} from './model-resource/model-resource';

// NOTE: The modification check doesn't handle the scenario where the user changes formula and the formula and
// then undoes the change (e.g. changing definition from 'A / B' -> 'B / A' -> 'A / B'). We could handle this
// scenario with an isEqual check, but I think it's easier to reason about (and safe from edge cases) to consider
// any user changes to have modified the saved formula.
// NOTE: this shouldn't be used with a resolved saved formula, as that will always have referencedMetrics field.
export function isModifiedShowClauseFormula(formula: ShowClauseFormula): boolean {
  return Boolean(formula.referencedMetrics);
}

/** local helper to keep formula expansion DRY */
function resolveFormulaHelper({
  metricsResource,
  formula,
  multiAttribution,
}: {
  metricsResource: MetricsResource;
  formula: ShowClauseFormula;
  multiAttribution: MultiAttribution | undefined;
}): {
  pendingSavedFormulaStatus: null | ModelStatus;
  isUnmodifiedSavedFormula: boolean;
  unmodifiedSavedMeasurementFields: FormulaMeasurement;
  unmodifiedSavedFormulaFields: ShowClauseFormula;
} {
  const initialOutput = {
    pendingSavedFormulaStatus: null,
    isUnmodifiedSavedFormula: false,
    unmodifiedSavedMeasurementFields: {},
    unmodifiedSavedFormulaFields: {},
  };

  if (!formula.id) {
    // not a saved formula
    return initialOutput;
  }

  const model = metricsResource.getOne(formula.id);
  if (model.status !== ModelStatus.Success) {
    return {...initialOutput, pendingSavedFormulaStatus: model.status};
  }

  if (isModifiedShowClauseFormula(formula)) {
    return initialOutput;
  }

  const savedFormulaMetric = model.value;
  const measurement = savedFormulaMetric.definition.measurement;
  return {
    pendingSavedFormulaStatus: null,
    isUnmodifiedSavedFormula: true,
    unmodifiedSavedMeasurementFields: {
      ...(measurement?.cumulative ? {cumulative: measurement.cumulative} : {}),
      ...(measurement?.rolling ? {rolling: measurement.rolling} : {}),
      // multiAttribution is local rather than part of the saved formula
      ...(multiAttribution && {multiAttribution}),
    },
    unmodifiedSavedFormulaFields: {
      id: savedFormulaMetric.id,
      name: savedFormulaMetric.name,
      definition: savedFormulaMetric.definition.formula.definition,
      referencedMetrics: savedFormulaMetric.definition.formula.referencedMetrics,
    },
  };
}

/**
 * Returns QueryFormulaEntry (or a subset), adding the saved formula fields if the local formula is unmodified.
 *
 * Returns null if it's a saved formula that has not loaded
 */
export function getResolvedFormulaEntry<T extends Pick<QueryFormulaEntry, 'event' | 'multiAttribution'>>({
  metricsResource,
  formulaEntry,
}: {
  metricsResource: MetricsResource;
  formulaEntry: T;
}): T extends QueryFormulaEntry ? QueryFormulaEntry : Pick<QueryFormulaEntry, 'event' | 'multiAttribution'> | null {
  if (!formulaEntry.event) {
    return null;
  }

  const {
    pendingSavedFormulaStatus,
    isUnmodifiedSavedFormula,
    unmodifiedSavedMeasurementFields,
    unmodifiedSavedFormulaFields,
  } = resolveFormulaHelper({
    metricsResource,
    formula: formulaEntry.event,
    multiAttribution: formulaEntry.multiAttribution,
  });

  if (pendingSavedFormulaStatus) {
    return null;
  }

  if (!isUnmodifiedSavedFormula) {
    return cloneDeep(formulaEntry) as T extends QueryFormulaEntry
      ? QueryFormulaEntry
      : Pick<QueryFormulaEntry, 'event'>;
  }

  return cloneDeep({
    ...formulaEntry,
    event: {
      ...formulaEntry.event,
      ...unmodifiedSavedFormulaFields,
    },
    ...unmodifiedSavedMeasurementFields,
  }) as T extends QueryFormulaEntry ? QueryFormulaEntry : Pick<QueryFormulaEntry, 'event'>;
}

/**
 * Returns FormulaShowClause (non-class type), adding the saved formula fields if the local formula is unmodified.
 *
 * Returns `null` if it's a saved formula that has not loaded
 */
export function getResolvedFormulaShowClause({
  metricsResource,
  formulaShowClause,
}: {
  metricsResource: MetricsResource;
  formulaShowClause: FormulaShowClause;
}): FormulaShowClause | null {
  if (!formulaShowClause.formula) {
    return null;
  }

  const {
    pendingSavedFormulaStatus,
    isUnmodifiedSavedFormula,
    unmodifiedSavedMeasurementFields,
    unmodifiedSavedFormulaFields,
  } = resolveFormulaHelper({
    metricsResource,
    formula: formulaShowClause.formula,
    multiAttribution: formulaShowClause.measurement.multiAttribution,
  });

  if (pendingSavedFormulaStatus) {
    return null;
  }

  if (!isUnmodifiedSavedFormula) {
    return cloneDeep(formulaShowClause);
  }

  return cloneDeep({
    ...formulaShowClause,
    formula: {
      ...formulaShowClause.formula,
      ...unmodifiedSavedFormulaFields,
    },
    ...(!isEmpty(unmodifiedSavedMeasurementFields) ? {measurement: unmodifiedSavedMeasurementFields} : {}),
  });
}

/**
 * If the referenced metrics have changed, update the saved formula clause with these local modifications.
 */
export function maybeModifySavedFormula({
  formulaClass,
  metricsResource,
  updateReferencedMetricsFn,
}: {
  formulaClass: FormulaShowClauseClass;
  metricsResource: MetricsResource;
  updateReferencedMetricsFn: (referencedMetrics: BehaviorShowClause[]) => void;
}): void {
  const resolvedClause = getResolvedFormulaShowClause({
    formulaShowClause: formulaClass.toBookmarkData(),
    metricsResource,
  });
  if (!resolvedClause || !resolvedClause.formula.id) {
    return;
  }

  const oldReferencedMetrics = cloneDeep(resolvedClause.formula.referencedMetrics);
  updateReferencedMetricsFn(resolvedClause.formula.referencedMetrics);
  if (!isEqual(oldReferencedMetrics, resolvedClause.formula.referencedMetrics)) {
    formulaClass.formula = resolvedClause.formula;
    formulaClass.measurement = resolvedClause.measurement;
  }
}
